import React, { useState } from 'react';
import { motion, AnimatePresence, useDragControls } from 'motion/react';
import { formatCurrencyForDisplay } from '@/lib/utils/currencyFormat';
import type { EventBookingCategoryResponse } from '@/features/booking/types/eventBookingCategoryTypes';
import { MOBILE_POPUP_HEIGHT } from '@/features/booking/constants/mobileBooking';

interface PriceBreakdown {
  totalPrice: number;
  perGuestBreakdown: Array<{
    addOns: Array<{
      addOn: {
        add_on: {
          name: string;
        };
      };
      price: number;
    }>;
  }>;
}

interface MobileTableFloatingSummaryProps {
  priceBreakdown: PriceBreakdown;
  tableSlots?: Array<{
    tableNumber: string;
    tableSize: number;
    guestCount: number;
    basePrice: number;
  }>;
  guestCount: number;
  eventBookingCategory: EventBookingCategoryResponse | null;
  onAddToCart: () => void;
  addingToCart: boolean;
  buttonText: string;
  isUpdateMode: boolean;
  onUpdateCart?: () => void;
  cartId: string | null;
  addOnCount: number;
  isLoadingCarts: boolean;
  hasCartChanges: boolean;
  hasViewCartButton: boolean;
  hasCartItemForEBC: boolean;
  currency?: string;
  itemType?: string;
  onViewCart: () => void;
}

const MobileTableFloatingSummary: React.FC<MobileTableFloatingSummaryProps> = ({
  priceBreakdown,
  tableSlots = [],
  guestCount,
  eventBookingCategory,
  onAddToCart,
  addingToCart,
  buttonText,
  isUpdateMode,
  onUpdateCart,
  addOnCount,
  isLoadingCarts,
  hasCartChanges,
  hasViewCartButton,
  hasCartItemForEBC,
  currency = 'USD',
  itemType,
  onViewCart
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const dragControls = useDragControls();
  const formatPrice = (amount: number) => formatCurrencyForDisplay(amount, currency);

  const isButtonDisabled = isLoadingCarts || addingToCart || (isUpdateMode && !hasCartChanges && !(hasCartItemForEBC && guestCount === 0));

  if (priceBreakdown.totalPrice === 0 && !hasCartItemForEBC) {
    return null;
  }

  return (
    <>
      {/* Backdrop for expanded state */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsExpanded(false)}
            className="fixed inset-0 bg-black/60 z-50"
            style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
          />
        )}
      </AnimatePresence>

      {/* Sticky Bottom Bar Container */}
      <motion.div
        className="fixed bottom-0 left-0 right-0 z-[65] flex flex-col justify-end pointer-events-auto"
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        {/* Expanded Content (Drawer) positioned behind the bar */}
        <div className="absolute bottom-0 left-0 right-0 -z-10 w-full flex flex-col pointer-events-none">
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ y: "100%", opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: "100%", opacity: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="flex flex-col pt-3 relative rounded-t-[32px] bg-[#0a0a0a] border-t border-white/10 shadow-[0_-10px_40px_rgba(0,0,0,0.5)] overflow-hidden pointer-events-auto"
                style={{ 
                  height: `calc(${MOBILE_POPUP_HEIGHT} + 80px)`, 
                  paddingBottom: 'calc(80px + env(safe-area-inset-bottom, 0px))' 
                }}
                drag="y"
                dragControls={dragControls}
                dragListener={false}
                dragConstraints={{ top: 0, bottom: 300 }}
                dragElastic={0.3}
                onDragEnd={(_, info) => {
                  if (info.offset.y > 80 || info.velocity.y > 400) {
                    setIsExpanded(false);
                  }
                }}
              >
                {/* Fixed Top Header — drag handle */}
                <div 
                  className="bg-[#0a0a0a] px-6 pt-5 pb-2 z-20 shrink-0 border-b border-white/5 relative shadow-sm flex flex-col items-center cursor-grab active:cursor-grabbing touch-none"
                  onPointerDown={(e) => dragControls.start(e)}
                >
                  {/* Slider Handle */}
                  <div className="top-3 w-12 h-1.5 rounded-full pointer-events-none transition-colors duration-300 bg-gray-600 absolute" />
                  <h3 className="text-white font-bold text-xl mt-2 self-start">Summary</h3>
                </div>

                {/* Scrollable Body */}
                <div 
                  className="overflow-y-auto overscroll-contain scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800 flex-1"
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  
                  <div className="space-y-4 px-6 pt-2 pb-4">
                    {/* Guest Count */}
                    <div className="flex justify-between items-center py-2 border-b border-gray-700/50 text-sm">
                      <span className="text-gray-400">Total Guests</span>
                      <span className="text-white font-medium">{guestCount}</span>
                    </div>

                    {/* Table Info */}
                    {tableSlots && tableSlots.filter(slot => slot.guestCount > 0).length > 0 && (
                      <>
                        <div className="flex justify-between items-center py-2 border-b border-gray-700/50 text-sm">
                          <span className="text-gray-400">Price per seat</span>
                          <span className="text-white font-medium">
                            {formatPrice(eventBookingCategory?.price || 0)}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-2 py-2 border-b border-gray-700/50">
                          <span className="text-gray-500 text-xs uppercase tracking-wider">{itemType || 'Table'}</span>
                          <span className="text-gray-500 text-xs uppercase tracking-wider text-center">Size</span>
                          <span className="text-gray-500 text-xs uppercase tracking-wider text-right">Price</span>
                        </div>
                        
                        {tableSlots.filter(slot => slot.guestCount > 0).map((slot, index) => (
                          <div key={`${slot.tableNumber}-${index}`} className="grid grid-cols-3 gap-2 py-2 border-b border-gray-700/50 text-sm">
                            <span className="text-white font-medium">{slot.tableNumber}</span>
                            <span className="text-white font-medium text-center">{slot.tableSize}</span>
                            <span className="text-white font-medium text-right">{formatPrice(slot.basePrice)}</span>
                          </div>
                        ))}
                      </>
                    )}

                    {/* Add-ons */}
                    {priceBreakdown?.perGuestBreakdown?.map((guest, guestIndex) => {
                      const aggregatedAddOns = guest.addOns.reduce((acc, addOn) => {
                        const name = addOn.addOn.add_on.name;
                        if (!acc[name]) {
                          acc[name] = { count: 0, unitPrice: addOn.price, totalPrice: 0 };
                        }
                        acc[name].count++;
                        acc[name].totalPrice += addOn.price;
                        return acc;
                      }, {} as Record<string, { count: number; unitPrice: number; totalPrice: number }>);
                      
                      const addOnEntries = Object.entries(aggregatedAddOns);
                      
                      return addOnEntries.length > 0 ? (
                        <div key={guestIndex} className="py-2 border-b border-gray-700/50">
                          <div className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 px-1">Guest {guestIndex + 1} Add-ons</div>
                          <div className="flex flex-col gap-1.5 px-1">
                            {addOnEntries.map(([name, { count, totalPrice }], addOnIndex) => (
                              <div key={addOnIndex} className="flex justify-between items-center text-base font-medium">
                                <span className="text-gray-300">
                                  {name}{count > 1 && <span className="text-blue-400 font-bold ml-1.5 border border-blue-500/30 bg-blue-500/10 px-1 rounded">×{count}</span>}
                                </span>
                                <span className="text-white font-bold">{formatPrice(totalPrice)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>

                {/* Fixed Total */}
                <div className="px-6 pt-2 pb-0 mt-auto">
                  {/* Total row at bottom of scroll area */}
                  <div className="flex justify-between items-center pt-4 mt-2 border-t border-white/10 px-1">
                    <span className="text-white font-bold text-lg">Total</span>
                    <span className="text-blue-400 font-black text-2xl">
                      {formatPrice(priceBreakdown.totalPrice)}
                    </span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Persistent Bottom Bar */}
        <div className="bg-[#0a0a0a] z-10 flex flex-col items-center justify-between border-t border-white/5 relative">
            <div className="w-full px-5 pt-6 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] flex items-center justify-between gap-4">
              {/* Left: Price and Expand Toggle */}
              <div 
                className="flex-1 flex flex-col justify-center cursor-pointer min-w-0"
                onClick={() => setIsExpanded(!isExpanded)}
              >
            <div className="flex items-center gap-2">
               <span className="text-xl font-bold text-white truncate">
                {formatPrice(priceBreakdown.totalPrice)}
              </span>
              <svg 
                className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </div>
            
            <div className="text-xs text-gray-400 truncate mt-0.5 font-medium">
              {guestCount} {guestCount === 1 ? 'guest' : 'guests'} • {addOnCount} {addOnCount === 1 ? 'addon' : 'addons'}
            </div>
          </div>

          {/* Right: Actions */}
          <div className={`flex gap-2 shrink-0 ${!hasViewCartButton ? 'flex-1' : ''}`}>
            {hasViewCartButton && (
              <button
                onClick={onViewCart}
                className="px-5 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm shadow-[0_0_20px_rgba(37,99,235,0.4)] transition-all active:scale-95 border-transparent"
              >
                View Cart
              </button>
            )}

            <button
              onClick={() => {
                if (isUpdateMode && onUpdateCart) {
                  onUpdateCart();
                } else {
                  onAddToCart();
                }
                setIsExpanded(false);
              }}
              disabled={isButtonDisabled}
              className={`px-5 py-3 rounded-xl font-bold text-sm shadow-lg transition-all border outline-none overflow-hidden relative active:scale-95 flex items-center justify-center ${
                 !hasViewCartButton ? 'flex-1' : ''
              } ${
                 isButtonDisabled
                  ? 'bg-gray-800 text-gray-500 cursor-not-allowed border-transparent'
                  : hasCartItemForEBC && guestCount === 0
                  ? 'bg-amber-400 hover:bg-amber-300 text-black border-transparent shadow-[0_0_15px_rgba(251,191,36,0.2)]'
                  : hasCartItemForEBC && !hasCartChanges
                  ? 'bg-transparent text-gray-400 border-gray-800 cursor-not-allowed'
                  : hasCartItemForEBC && hasCartChanges
                  ? 'bg-amber-400 hover:bg-amber-300 text-black border-transparent shadow-[0_0_15px_rgba(251,191,36,0.2)]'
                  : 'bg-blue-600 hover:bg-blue-500 text-white border-transparent shadow-[0_0_15px_rgba(37,99,235,0.4)]'
              }`}
            >
              {addingToCart ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-current rounded-full border-t-transparent animate-spin" />
                  <span>Adding...</span>
                </div>
              ) : hasCartItemForEBC && !hasCartChanges ? (
                <div className="flex items-center justify-center gap-1.5 opacity-90">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>In Cart</span>
                </div>
              ) : (
                <div className={`flex items-center justify-center gap-1.5 opacity-90 font-semibold ${hasCartItemForEBC && hasCartChanges && guestCount > 0 ? 'text-black' : 'text-white'}`}>
                  <span>{hasCartItemForEBC && guestCount === 0 ? 'Update' : buttonText}</span>
                </div>
              )}
            </button>
          </div>

            </div>
        </div>
      </motion.div>
    </>
  );
};

export default MobileTableFloatingSummary;
